// CTV-445: shared framing for the PDF's ZIP choropleth. Both the report page
// (which fetches the Mapbox static basemap) and the PDF exporter (which projects
// the polygons) call computeZipMapFrame on the same ZIP set, so the basemap
// image and the vector overlay land on the exact same geographic window.

export interface ZipFramePoint {
  latitude: number;
  longitude: number;
  impressions: number;
}

export interface ZipMapFrame {
  minLng: number;
  maxLng: number;
  minLat: number;
  maxLat: number;
}

// Web-Mercator Y for a latitude (radian-scale, matching the deck.gl/Mapbox
// projection). Exported so callers can size the basemap to the frame's aspect.
export function mercatorY(lat: number): number {
  const c = Math.max(-85, Math.min(85, lat));
  return Math.log(Math.tan(Math.PI / 4 + (c * Math.PI) / 360));
}

// Frame on the MAIN delivery area: the bbox of the highest-volume ZIPs covering
// ~70% of plays, padded ~30% (with a floor so a 1-ZIP core isn't infinitely
// zoomed). Far-flung outliers don't shrink the view. Null when no ZIPs.
export function computeZipMapFrame(zips: ZipFramePoint[]): ZipMapFrame | null {
  if (zips.length === 0) return null;
  const sorted = [...zips].sort((a, b) => b.impressions - a.impressions);
  const total = sorted.reduce((s, z) => s + z.impressions, 0) || 1;
  const core: ZipFramePoint[] = [];
  let acc = 0;
  for (const z of sorted) {
    core.push(z);
    acc += z.impressions;
    if (acc / total >= 0.7) break;
  }
  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (const z of core) {
    minLng = Math.min(minLng, z.longitude);
    maxLng = Math.max(maxLng, z.longitude);
    minLat = Math.min(minLat, z.latitude);
    maxLat = Math.max(maxLat, z.latitude);
  }
  if (!isFinite(minLng)) return null;
  const padLng = Math.max((maxLng - minLng) * 0.3, 0.05);
  const padLat = Math.max((maxLat - minLat) * 0.3, 0.05);
  return { minLng: minLng - padLng, maxLng: maxLng + padLng, minLat: minLat - padLat, maxLat: maxLat + padLat };
}

// Mapbox Static Images URL for the frame (light basemap), sized so its pixel
// aspect matches the frame's mercator aspect — that way Mapbox renders exactly
// the bbox (no aspect padding) and the polygons align. Returns null without a
// token.
export function zipBasemapUrl(frame: ZipMapFrame, token: string | undefined): string | null {
  if (!token) return null;
  const spanXrad = ((frame.maxLng - frame.minLng) * Math.PI) / 180;
  const spanYmerc = mercatorY(frame.maxLat) - mercatorY(frame.minLat);
  if (spanXrad <= 0 || spanYmerc <= 0) return null;
  const W = 1000;
  const H = Math.max(1, Math.min(1280, Math.round((W * spanYmerc) / spanXrad)));
  const bbox = `[${frame.minLng},${frame.minLat},${frame.maxLng},${frame.maxLat}]`;
  return (
    `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/${bbox}/${W}x${H}` +
    `?access_token=${token}&attribution=false&logo=false`
  );
}
